import { useState, useEffect, useCallback } from "react"
import TestResultService from "../../../services/test_result_service"
import { useTenant } from "../../../hooks/useTenant"
import { toast } from "react-toastify"
import { toISODate } from "../../../utils/date"
export const useTestFormLogic = ({
    idActivity,
    activeEvent,
    members,
    excludedIds,
    withLoading
}) => {
    const { idTenant, user } = useTenant()
    const [testDrafts, setTestDrafts] = useState({}) // { idMember: { result: '', notes: '' } }

    // Preencher rascunhos com resultados existentes
    useEffect(() => {
        if (!idTenant || !idActivity || !activeEvent?.id) return

        const loadExistingResults = async () => {
            try {
                await withLoading('loadTests', async () => {
                    const results = await TestResultService.getResultsByActivity(idTenant, idActivity, activeEvent.id)
                    const drafts = {}

                    results.forEach(r => {
                        const measureType = activeEvent.testType;
                        const isDistanceMetric = ['fixed_time', 'distance'].includes(measureType);
                        const resultVal = isDistanceMetric ? String(r.resultDistance || '') : String(r.resultTime || '');

                        drafts[String(r.idMember || r.idMember)] = {
                            result: resultVal,
                            notes: r.notes || ''
                        }
                    })

                    setTestDrafts(drafts)
                })
            } catch (error) {
                console.error("Erro ao carregar resultados existentes:", error)
            }
        }

        loadExistingResults()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idTenant, idActivity, activeEvent?.id, withLoading])

    const handleResultChange = (idMember, value) => {
        setTestDrafts(prev => ({
            ...prev,
            [idMember]: {
                ...(prev[idMember] || {}),
                result: value
            }
        }))
    }

    const handleNotesChange = (idMember, value) => {
        setTestDrafts(prev => ({
            ...prev,
            [idMember]: {
                ...(prev[idMember] || {}),
                notes: value
            }
        }))
    }

    const saveTests = useCallback(async () => {
        if (!activeEvent || !idActivity) return

        if (!user || !user.uid) {
            toast.error("Usuário não autenticado.")
            return
        }

        const excludedSet = new Set(excludedIds || [])
        const testMembers = members.filter(c => !excludedSet.has(String(c.id)))

        if (testMembers.length === 0) {
            toast.warning("Nenhum aluno selecionado para o teste")
            return
        }

        await withLoading('saveTests', async () => {
            try {
                const payloads = []

                for (const member of testMembers) {
                    const idMember = String(member.id)
                    const draft = testDrafts[idMember]

                    if (!draft || !draft.result) continue // Pula alunos sem resultado

                    const payload = {
                        idMember: idMember,
                        memberName: member.fullName || member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Desconhecido',
                        memberGender: member.gender || null,
                        memberBirthDate: member.birthDate || member.dateOfBirth || null,
                        idActivity: idActivity,
                        idEvent: activeEvent.id,
                        idEmployee: user.uid,
                        employeeName: user.displayName || user.fullName || user.email || 'Sistema',
                        date: toISODate(new Date()),
                        type: activeEvent.testType || 'fixed_time',
                        notes: draft.notes || ""
                    }

                    if (['fixed_time', 'distance'].includes(activeEvent.testType)) {
                        const distValue = String(draft.result).replace(',', '.')
                        payload.resultDistance = parseFloat(distValue) || 0
                        payload.resultTime = null
                    } else {
                        payload.resultTime = draft.result
                        payload.resultDistance = null
                    }

                    payloads.push(payload)
                }

                if (payloads.length === 0) {
                    toast.warning("Nenhum resultado preenchido para salvar.")
                    return
                }

                await TestResultService.saveResults(idTenant, payloads)
                toast.success("Resultados de testes salvos com sucesso!")
            } catch (error) {
                console.error("Erro ao salvar testes:", error)
                toast.error(error.message || "Erro ao salvar resultados")
            }
        })
    }, [idTenant, idActivity, activeEvent, members, excludedIds, withLoading, testDrafts, user])

    return {
        testDrafts,
        handleResultChange,
        handleNotesChange,
        saveTests,
        dirtyCount: Object.entries(testDrafts).filter(([idMember, d]) => {
            const isExcluded = new Set(excludedIds || []).has(String(idMember))
            return !isExcluded && !!d.result
        }).length
    }
}
